import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable, of, Subscription, throwError, zip } from 'rxjs';
import { mapValues } from 'lodash';
import { SiteService, SiteServiceModel } from '../origami-api/services/site.service';
import { UserService, UserServiceModel } from '../origami-api/services/user.service';
import { ConfigServiceOptions } from '../origami-api/services/config.service';
import { SettingsService } from '@app/services/settings/settings.service';
import { MainSettingsModule } from '../services/settings/main';
import { RoleLevel, roleLevelToString } from '../origami-api/constants/role-levels';
import { MixpanelService } from '../shared/misc/analytics/mixpanel.service';
import { OpenPanelService } from '../shared/misc/analytics/open-panel.service';
import { User } from './user-details';
import { ErrorCode, FieldNames, FieldType, Locale, TopicTypeId } from '@app/origami-api/constants';
import { AuthService } from '@app/services/auth.service';
import { LoginToRoomService } from '@app/services/login-to-room.service';
import { SessionMonitorService } from './session-monitor.service';
import { InactivityMonitorService } from './inactivity-monitor.service';
import { DefaultForms } from '@app/session/default-forms';
import { DefaultFormsService } from '@app/services/default-forms.service';
import { SponsorService, SponsorServiceModel } from '@app/origami-api/services/sponsor.service';
import { catchError, concatMap, map, switchMap, take } from 'rxjs/operators';
import { LocaleSwitcher } from './locales-switcher.service';
import { UserGroupService, UserGroupServiceModel } from '@app/origami-api/services/user-group.service';
import { AppService } from '@app/app.service';
import { getServerConfig } from '@app/origami-api/server-config';
import {
  ContactTypeTitles,
  InvSiteCustomForm,
  InvSiteCustomFormService,
} from '@app/services/inv-site-custom-form.service';
import { GlobalEventService, OldUiMessages } from '@app/services/global-event.service';
import { TimeZoneInfo, TopicUtils } from '@app/origami-api/utils/topic-utils';
import { DateFormatStore } from './date-format-store';
import { NotificationsService } from '@app/services/notifications/notifications.service';
import { BackgroundJobsService } from '@app/services/background-jobs/background-jobs.service';
import { ObjectTypes } from '@app/origami-api/constants/object-types';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';
import { updateTopicTypes } from '@app/origami-api/constants/topic-type-names';
import { LocalStorageKeys } from '@app/services/local-storage.service';
import { UserZoom } from './user-preferences';
import { ErrorDescriptionService } from '@app/origami-api/services/error-description.service';
import { getErrorDescription } from '@app/origami-api/utils/error-utils';
import { updateErrorDescriptions } from '@app/origami-api/constants/errorDescriptionsFromBE';
import { DefaultFieldTitlesService } from '@app/origami-api/services/default-field-titles.service';
import { ChangeRoleService } from '@app/session/change-role.service';
import { getQueryParamValue } from '../../init';
import { fixLocale } from '@app/origami-api/constants/default-locales';
import { TopicSearchService } from '@app/origami-api/services/topic-search.service';
import { KpiTabDataService, RoomType } from '@app/origami-api/models/room-type';
import { RoomTypes } from '@app/origami-api/constants/room-types';
import { IGetIndexConfig } from '@app/origami-api/services/index-search.service';
import { RouteCheckers } from '@app/pages/main/route-checkers';
import { TopicTypesService } from '@app/origami-api/services/topic-types.service';
import { BuildProps } from '@app/origami-api/build-props';
import { CustomTopicTypes } from '@app/session/custom-topic-types';

export const ZoomDefault = 1.0;

interface SiteSessionInfo<T> {
  id: number;
  info: T;
  settings: MainSettingsModule;
  forms: DefaultForms;
  timeZone: TimeZoneInfo;
}

export class RoomSessionInfo implements SiteSessionInfo<SiteServiceModel.LoginToSiteResult> {
  public sponsor: SponsorServiceModel.Sponsor;
  public readonly customTopicTypes = new CustomTopicTypes();

  constructor(
    public readonly id: number,
    public readonly info: SiteServiceModel.LoginToSiteResult,
    public readonly settings: MainSettingsModule,
    public readonly forms: DefaultForms,
    public readonly invSiteCustomForm: InvSiteCustomForm,
    public readonly roomType: RoomType,
    private readonly sponsorService: SponsorService,
  ) {}

  //@deprecated
  getCurrentSponsor(): Observable<SponsorServiceModel.Sponsor> {
    if (this.sponsor) {
      return of(this.sponsor);
    }
    return this.sponsorService.getSponsor(this.info.SponsorId).pipe(map((res) => (this.sponsor = res.Result)));
  }

  get timeZone(): TimeZoneInfo {
    return this.info.RoomTimeZone;
  }

  get isCWS(): boolean {
    return this.info?.RoomTypeId === RoomTypes.Collaborate;
  }
}

export type DomainSessionInfo = SiteSessionInfo<SiteServiceModel.Site>;

@Injectable()
export class SessionService implements OnDestroy {
  get currentRoomId(): number {
    return this._room ? this._room.id : this._domain ? this._domain.id : null;
  }

  get currentRoomTypeId(): RoomTypes {
    return this._room ? this._room.roomType.id : null;
  }

  get domain(): DomainSessionInfo {
    return this._domain || ({} as any);
  }

  get room(): RoomSessionInfo {
    return this._room || ({} as any);
  }

  get user(): User {
    return this._roomUser || this._domainUser || ({} as any);
  }

  get forms(): DefaultForms {
    return this._room ? this._room.forms : this._domain ? this._domain.forms : null;
  }

  get settings(): MainSettingsModule {
    return this._room ? this._room.settings : this._domain ? this._domain.settings : null;
  }

  get navigationStartUrl(): string {
    return this._navigationStartUrl;
  }

  get navigationEndUrl(): string {
    return this._navigationEndUrl;
  }

  get locale(): Locale {
    return LocaleSwitcher.getCurrentLocale();
  }

  get userZoom(): UserZoom {
    return this._userZoom.getValue();
  }
  get userZoomObs(): Observable<UserZoom> {
    return this._userZoom.asObservable();
  }
  get savedUserZoom(): UserZoom {
    return this._savedUserZoom.getValue();
  }
  get isDomain(): boolean {
    return this.currentRoomId === this.domain.id;
  }
  get searchIndexConfig(): IGetIndexConfig[] {
    return this._searchIndexConfig.get(this.currentRoomId);
  }
  private _userZoom = new BehaviorSubject<UserZoom>({ default: ZoomDefault });
  private _savedUserZoom = new BehaviorSubject<UserZoom>({ default: ZoomDefault });

  get enterRoomObs() {
    return this._enterRoomBSubj.asObservable();
  }
  private _enterRoomBSubj = new BehaviorSubject<boolean>(false);

  get contactTypeTitles(): ContactTypeTitles {
    return (
      (this.room.invSiteCustomForm && this.room.invSiteCustomForm.contactTypeTitles) ||
      tiStrings.services.contactTypeTitles.stringsContactTypeTitles
    );
  }

  get actualAppVersion() {
    let originalVersion = (this.domain.settings.general.general.buildVersionReplaceFrom.get() || '').split(' ')[0];

    let displayVersion;

    if (!BuildProps.OrigamiBuildNumber || originalVersion == BuildProps.OrigamiBuildNumber) {
      displayVersion = this.domain.settings.general.general.buildVersionReplaceTo.get();
    } else {
      displayVersion = BuildProps.OrigamiBuildNumber;
    }

    return (displayVersion || '').split(' ')[0];
  }

  private _userGroups: UserGroupServiceModel.UserGroup[] = [];
  private _domainUser: User;
  private _roomUser: User;
  private _domain: DomainSessionInfo;
  private _room: RoomSessionInfo;
  private _navigationStartUrl: string;
  private _navigationEndUrl: string;
  private _searchIndexConfig = new Map<number, IGetIndexConfig[]>();
  private subs = new Subscription();

  private readonly availableLocales = Object.values(Locale);

  constructor(
    public readonly auth: AuthService,
    public readonly inactivityMonitorService: InactivityMonitorService,
    private siteService: SiteService,
    private userService: UserService,
    private topicTypesService: TopicTypesService,
    private settingsService: SettingsService,
    private loginToSiteService: LoginToRoomService,
    private mixpanel: MixpanelService,
    private openPanel: OpenPanelService,
    private sessionMonitorService: SessionMonitorService,
    private backgroundJobsService: BackgroundJobsService,
    private defaultFormsService: DefaultFormsService,
    private router: Router,
    private sponsorService: SponsorService,
    private userGroupService: UserGroupService,
    private appService: AppService,
    private contactTitlesService: InvSiteCustomFormService,
    private globalEventService: GlobalEventService,
    private notificationsService: NotificationsService,
    private errorDescriptionService: ErrorDescriptionService,
    private defaultFieldTitlesService: DefaultFieldTitlesService,
    private changeRoleService: ChangeRoleService,
    private topicSearchService: TopicSearchService,

    public kpiTabDataService: KpiTabDataService,
    @Optional() @SkipSelf() sessionService: SessionService,
  ) {
    if (sessionService) {
      throw new Error('SessionService can be provided only by SessionModule');
    }

    this.changeRoleService.dialogService.sessionService = this;
    this.loginToSiteService.dialogService.sessionService = this;

    // todo just for development
    (window as any).SESSION = this;
    // just marker for old UI to recognize the new one
    (window as any)['MODERN_UI'] = true;

    this.subs.add(this.globalEventService.on(OldUiMessages.updateSession).subscribe(() => window.location.reload()));

    this.subs.add(
      this.globalEventService.on(OldUiMessages.startInactive).subscribe(() => this.inactivityMonitorService.showMask()),
    );

    this.subs.add(
      this.globalEventService.on(OldUiMessages.startJobResult).subscribe((data: any) => {
        const jobIds = data.JobIds;
        for (let jobId of jobIds) {
          const job = this.backgroundJobsService.add(
            data.siteId,
            jobId,
            tiStrings.session.sessionService.copyRequiredTitle,
          );
          job.change.subscribe((jobResponse) => {
            if (jobResponse.completed && jobResponse.completedWithErrors) {
              (jobResponse.result.ErrorRecords || []).forEach((error) => {
                let message = getErrorDescription(+error.ErrorCode);
                if (error.ErrorCode === ErrorCode.DOCUMENT_TYPE_IMPORT_FAILED) {
                  message = tiStringsFormat(
                    tiStrings.session.sessionService.docTypeErrorMessagePattern,
                    error.ErrorMessage.replace(/[\{\}]/gi, ''),
                    message,
                  );
                } else if (error.ErrorCode === ErrorCode.TOPIC_IS_REFERENCED) {
                  message = tiStringsFormat(
                    tiStrings.session.sessionService.irbModifyErrorMessagePattern,
                    error.ObjectName,
                    ObjectTypes[error.RelatedObjectTypeId],
                    error.SiteName,
                  );
                } else {
                  message =
                    error.ObjectType === 'C'
                      ? tiStringsFormat(
                          tiStrings.session.sessionService.countryErrorMessagePattern,
                          error.ObjectName,
                          message,
                        )
                      : tiStringsFormat(
                          tiStrings.session.sessionService.irbErrorMessagePattern,
                          error.ObjectName,
                          message,
                        );
                }
                this.notificationsService.showError(message, tiStrings.session.sessionService.copyRequiredTitle);
              });
            }
          });
        }
      }),
    );

    this.subs.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this._navigationStartUrl = event.url;
        }
        if (event instanceof NavigationEnd) {
          this._navigationEndUrl = event.url;
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  enterDomain(): Observable<SessionService> {
    if (this._domain) {
      return of(this);
    }

    return zip(
      this.siteService.getSites({ isSystem: true }),
      this.userService.getUserDetails({ siteId: -1 }, null, true),
      this.topicTypesService.getTopicTypes(),
      this.settingsService.loadDomainSettings(
        [
          ConfigServiceOptions.ModuleName.Security,
          ConfigServiceOptions.ModuleName.General,
          ConfigServiceOptions.ModuleName.Reports,
          ConfigServiceOptions.ModuleName.Email,
          ConfigServiceOptions.ModuleName.Program,
          ConfigServiceOptions.ModuleName.GridConfiguration,
          ConfigServiceOptions.ModuleName.ViewModeConfiguration,
          ConfigServiceOptions.ModuleName.UiConfiguration,
        ],
        true,
      ),
    ).pipe(
      map(([site, user, topicTypesRes, mainSettings]) => {
        updateTopicTypes(topicTypesRes.Result);

        const siteInfo = site.Result ? (site.Result.SiteDetails ? site.Result.SiteDetails[0] : null) : null;

        if (siteInfo) {
          this.settingsService.setDomainId(siteInfo.SiteId);
          this._domain = {
            id: siteInfo.SiteId,
            info: siteInfo,
            settings: mainSettings,
            forms: new DefaultForms(siteInfo.SiteId, this.domain.id, this.defaultFormsService),
            timeZone: user.Result.ServerTimeZone,
          };
        }

        this._domainUser = new User({
          currentRoomId: siteInfo ? siteInfo.SiteId : null,
          details: user.Result,
          userService: this.userService,
          userGroupService: this.userGroupService,
          sessionService: this,
        });

        this.setExpiredString();

        // we do not need monitoring on dev environment
        if (BuildProps.OrigamiBuildNumber) {
          this.updateMixPanel(this._domainUser.details);
          this.updateOpenPanel(this._domainUser.details);
        }

        this.sessionMonitorService.start();
        this.startInactivityMonitor();

        this.notificationsService.setRoomInfo(this._room || this._domain);

        DateFormatStore.userDateFormat = TopicUtils.getTopicFieldValue(
          this.user.details.GlobalUserTopic,
          FieldNames.User.CustomDateFormat,
        );

        const explicitLocale = fixLocale(getQueryParamValue('lang')) as Locale;

        let defaultLocale =
          explicitLocale ||
          this.user.preferences.locale ||
          mainSettings.general.general.defaultLocale.get() ||
          getServerConfig().defaultLocale ||
          Locale.en_US;

        if (!this.availableLocales.includes(defaultLocale as Locale)) {
          defaultLocale = Locale.en_US; // falling back to default one
        }

        LocaleSwitcher.setLocale(defaultLocale, true);
        this.setErrorDescription(defaultLocale);
        this.setDefaultFieldTitles(defaultLocale);

        this._userZoom.next(this.user.preferences.userZoom);
        this._savedUserZoom.next(this.user.preferences.userZoom);
        return this;
      }),
    );
  }

  leaveDomain(): Observable<SessionService> {
    this.sessionMonitorService.stop();
    this.inactivityMonitorService.stop();
    this._domain && this.defaultFormsService.clear(this._domain.id);

    this._domain = null;
    this._domainUser = null;
    this.notificationsService.setRoomInfo(this._room || this._domain);
    DateFormatStore.clearUserDateFormat();
    return of(this);
  }

  enterRoom(roomId: number, force?: boolean): Observable<SessionService> {
    if (this._room && !force) {
      return of(this);
    }

    const settingModules = Object.keys(ConfigServiceOptions.ModuleName).map((name) => {
      return ConfigServiceOptions.ModuleName[name];
    }) as ConfigServiceOptions.ModuleName[];

    return zip(
      this.loginToSiteService.login(roomId),
      this.userService.getUserDetails({ siteId: roomId }),
      this.settingsService.loadRoomSettings(roomId, settingModules, force),
      this.contactTitlesService.getInvSiteCustomForm(roomId),
      this.topicSearchService.searchTopics({ siteId: this.domain.id, topicTypeId: TopicTypeId.RoomType }),
    ).pipe(
      switchMap(([siteInfo, user, settings, invSiteCustomForm, roomTypeTopics]) => {
        const roomTypeTopic = roomTypeTopics.Result.TopicList.find(
          (topic) =>
            TopicUtils.getTopicFieldNumberValue(topic, FieldNames.RoomType.RoomTypeId) === siteInfo.details.RoomTypeId,
        );
        const roomType = roomTypeTopic ? RoomType.create(roomTypeTopic, this.kpiTabDataService) : null; // something wrong

        this._room = siteInfo
          ? new RoomSessionInfo(
              roomId,
              siteInfo.details,
              settings,
              new DefaultForms(roomId, this.domain.id, this.defaultFormsService),
              invSiteCustomForm,
              roomType,
              this.sponsorService,
            )
          : null;

        this.setExpiredString();

        // we need to patch user's role in one case: TIN-10676
        if (user.Result.AdditionalInfo.IsUserMyTiCRA && user.Result.RoleLevel === RoleLevel.EDITOR) {
          user.Result.RoleLevel = RoleLevel.READER;
          user.Result.IsRoleChange = true;
        }

        DateFormatStore.userDateFormat = TopicUtils.getTopicFieldValue(
          this.user.details.GlobalUserTopic,
          FieldNames.User.CustomDateFormat,
        );
        DateFormatStore.roomDateFormat = this.room.settings.general.general.dateFormat.get();

        this.startInactivityMonitor();

        this.notificationsService.setRoomInfo(this._room || this._domain);

        this._roomUser = new User({
          currentRoomId: siteInfo ? siteInfo.id : null,
          details: user.Result,
          userService: this.userService,
          userGroupService: this.userGroupService,
          sessionService: this,
        });
        //console.log('user', user);
        //console.log('this._roomUser', this._roomUser);

        if (
          user.Result.AdditionalInfo.IsUserMyTiCRA &&
          user.Result.IsRoleChange &&
          user.Result.RoleLevel === RoleLevel.READER
        ) {
          this.changeRoleService.changeUserRoleShowModal();
        }

        this.siteService.proxy.settings = settings;
        this.setUpUserAccess();

        const sponsor$ = RouteCheckers.isTemplateSite(this) ? of(null) : this.room.getCurrentSponsor();
        return forkJoin([sponsor$, this.loadCustomTopicTypes$(roomId)]).pipe(
          map(() => {
            this._enterRoomBSubj.next(true);
            return this;
          }),
          catchError((error) => this.leaveRoom().pipe(concatMap(() => throwError(() => error)))),
        );
      }),
    );
  }

  leaveRoom(): Observable<SessionService> {
    this.loginToSiteService.logout();
    this.settingsService.clear();
    this._room && this.defaultFormsService.clear(this._room.id);
    this._room = null;
    this._roomUser = null;
    this.startInactivityMonitor();
    this.notificationsService.setRoomInfo(this._room || this._domain);
    DateFormatStore.clearRoomDateFormat();
    this.setExpiredString();

    return of(this);
  }

  reloadCustomTopicTypes(): Observable<void> {
    return this.loadCustomTopicTypes$(this._room?.id);
  }

  // TODO(QMS-1919): drop the QMS gate once custom topic types are available across other modules.
  private loadCustomTopicTypes$(roomId: number): Observable<void> {
    if (!this._room?.settings.general.module.enableQMS.get()) {
      this._room?.customTopicTypes.clear();
      return of(undefined);
    }
    return this.topicTypesService.getTopicTypes({ isCustom: true, siteId: roomId }, null, null, true).pipe(
      switchMap((res) => {
        this._room?.customTopicTypes.set(res.Result || []);
        return this.loadCustomFormNames$(roomId);
      }),
    );
  }

  /**
   * A custom form is titled by its own FormName, which is editable in the form settings, so the names
   * have to be read from the forms themselves rather than from the topic types (QMS-2502).
   */
  private loadCustomFormNames$(roomId: number): Observable<void> {
    const topicTypeIds = this._room?.customTopicTypes.ids ?? [];
    if (!topicTypeIds.length) return of(undefined);

    return this.defaultFormsService.getFormsById(roomId, topicTypeIds).pipe(
      take(1),
      map((formsByTopicTypeId) => {
        this._room?.customTopicTypes.setFormNames(mapValues(formsByTopicTypeId, (form) => form?.FormName));
      }),
      // titles are cosmetic - a failure here must never keep the user out of the room
      catchError(() => of(undefined)),
    );
  }

  setLocale(locale: Locale, persistent?: boolean): void {
    if (locale === this.locale) {
      return;
    }
    if (!persistent) {
      this.appService.removeBeforeUnloadListener();
      LocaleSwitcher.setLocale(locale);
      this.setErrorDescription(locale);
      this.setDefaultFieldTitles(locale);
      return;
    }

    this.user.preferences
      .save(this.domain.id, true, {
        FieldName: FieldNames.User.Locale,
        FieldType: FieldType.Text,
        Fieldvalue: locale,
      })
      .pipe(take(1))
      .subscribe((success) => {
        if (success) {
          this.appService.removeBeforeUnloadListener();
          LocaleSwitcher.setLocale(locale);
          this.setErrorDescription(locale);
          this.setDefaultFieldTitles(locale);
        }
      });
  }

  setUserZoom(userZoom: UserZoom, save = false): void {
    if (save) {
      if (this._userZoom && userZoom.default === this._savedUserZoom.getValue().default) {
        return;
      }
    } else {
      if (this._userZoom && userZoom.default === this._userZoom.getValue().default) {
        return;
      }
    }

    this._userZoom.next(userZoom);

    save && this._savedUserZoom.next(userZoom);
    save &&
      this.user.preferences
        .save(this.domain.id, true, {
          FieldName: FieldNames.User.UserZoom,
          FieldType: FieldType.Text,
          Fieldvalue: JSON.stringify({ data: userZoom }),
        })
        .pipe(take(1))
        .subscribe((success) => {
          if (success) {
            // this._savedUserZoom.next(userZoom);
          }
        });
  }

  private startInactivityMonitor() {
    const settings = this.room && this.room.settings ? this.room.settings : this.domain.settings;
    if (settings) {
      const idleTimer = settings.security.idleTimer;
      const logoutDelay = idleTimer.secondsToAprove.get();
      const timeToIdle = idleTimer.timeToIdle.get();
      const confirmationText = idleTimer.confirmationText.get();

      this.inactivityMonitorService.start(logoutDelay, timeToIdle, confirmationText);
    }
  }

  private updateMixPanel(userDetails: UserServiceModel.User) {
    const role = roleLevelToString(userDetails.MaxUserRoleLevelAcrossRooms);

    this.mixpanel.init();
    this.mixpanel.userLogout();
    this.mixpanel.setUserProperties({
      $email: userDetails.EmailAddress,
      $name: userDetails.FullName,
    });
    this.mixpanel.setUsername('' + userDetails.EmailAddress);
    this.mixpanel.setSuperProperties({
      User: userDetails.EmailAddress,
      Instance: window.location.hostname,
      UI: 'Modern',
      // todo need to fix this after sessionService update
      // Version: this.sessionService.origamiBuildNumber,
      // TODO: this should be changed for the room UI
      Role: role,
    });
  }

  private updateOpenPanel(userDetails: UserServiceModel.User) {
    const role = roleLevelToString(userDetails.MaxUserRoleLevelAcrossRooms);
    this.openPanel.syncUser(userDetails, role);
  }

  private setExpiredString() {
    const text = this.settings
      ? this.settings.security.idleTimer.alertText.get()
      : tiStrings.app.misc.idleTimerFallbackMessage;

    localStorage.setItem(LocalStorageKeys.ti_idle_timer_alert_text, text);
  }

  public setErrorDescription(locale?: string) {
    locale = locale || Locale.en_US;
    this.errorDescriptionService.getErrorDescriptions(locale).subscribe((res) => {
      updateErrorDescriptions(res);
    });
  }

  public setDefaultFieldTitles(locale?: string) {
    locale = locale || Locale.en_US;
    this.defaultFieldTitlesService.loadFieldTitles(locale).pipe(take(1)).subscribe();
  }

  setSearchIndexConfig(cfg: IGetIndexConfig[]) {
    this._searchIndexConfig.set(this.currentRoomId, [...cfg]);
  }

  private setUpUserAccess(): void {
    this.user
      .getGroups()
      .pipe(take(1))
      .subscribe((groups: UserGroupServiceModel.UserGroup[]) => {
        this._userGroups = groups;
      });
  }
}
